<div class="search-page">
  <header class="page-header">
    <div>
      <p class="eyebrow">Search</p>
      <h1>Find anything fast</h1>
      <p class="subtitle">Use separate lookups for invoices, tasks, customers, and revenue or expense transactions.</p>
    </div>
  </header>

  <div class="search-grid three-up">
    <section class="search-panel">
      <div class="panel-header">
        <div class="panel-kicker">Invoices</div>
        <h2>Search invoice</h2>
      </div>

      <section class="search-bar">
        <ap-search
          [fullWidth]="true"
          placeholder="invoice number"
          [query]="invoiceQuery"
          (onChange)="onInvoiceQueryChange($event)"
          (cancelled)="onInvoiceCancelled()"
        ></ap-search>

        <ap-search
          [fullWidth]="true"
          placeholder="invoice amount"
          [query]="invoiceAmountQuery"
          (onChange)="onInvoiceAmountQueryChange($event)"
          (cancelled)="onInvoiceAmountCancelled()"
        ></ap-search>

        <ap-search
          [fullWidth]="true"
          placeholder="line item description"
          [query]="invoiceLineItemQuery"
          (onChange)="onInvoiceLineItemQueryChange($event)"
          (cancelled)="onInvoiceLineItemCancelled()"
        ></ap-search>
      </section>

      <section class="results">
        <div *ngIf="isSearchingAnyInvoice && showInvoiceResults" class="searching inline">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          <div class="hint">Updating invoice matches...</div>
        </div>

        <div *ngIf="showInvoiceEmptyState" class="empty">
          <h3>Start typing to search</h3>
          <p>Search invoices by exact number, exact total amount, or line item description.</p>
        </div>

        <div *ngIf="invalidInvoiceQuery" class="empty">
          <h3>Invoice number only</h3>
          <p>This lookup currently supports invoice numbers only.</p>
        </div>

        <div *ngIf="invalidInvoiceAmountQuery" class="empty">
          <h3>Amount only</h3>
          <p>This lookup supports invoice totals only.</p>
        </div>

        <div *ngIf="showInvoiceNoResultsYet" class="empty">
          <h3>No invoice found</h3>
          <p>No invoice matched the current number, amount, or line item filters.</p>
        </div>

        <div *ngIf="isSearchingAnyInvoice && !showInvoiceResults" class="searching">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          <div class="hint">Searching invoices...</div>
        </div>

        <div *ngIf="showInvoiceResults" class="results-list">
          <button type="button" class="result-card" *ngFor="let invoice of combinedInvoices" (click)="openInvoice(invoice)">
            <div class="result-main">
              <div class="kicker">Invoice</div>
              <div class="title">#{{ invoice.number }}</div>
              <div class="meta">
                <span>{{ invoice.customer?.name || 'No customer' }}</span>
                <span class="dot">&middot;</span>
                <span>{{ invoice.dateCreated | date:'dd MMM yyyy' }}</span>
              </div>
            </div>
            <div class="result-side">
              <div class="amount">{{ invoice.total | human:true }}</div>
              <div class="status" [class]="invoice.status">{{ invoice.status || 'unknown' }}</div>
            </div>
          </button>
        </div>
      </section>
    </section>

    <section class="search-panel">
      <div class="panel-header">
        <div class="panel-kicker">Tasks</div>
        <h2>Search task number</h2>
      </div>

      <section class="search-bar">
        <ap-search
          [fullWidth]="true"
          placeholder="task number"
          [query]="taskQuery"
          (onChange)="onTaskQueryChange($event)"
          (cancelled)="onTaskCancelled()"
        ></ap-search>
      </section>

      <section class="results">
        <div *ngIf="isSearchingTasks && showTaskResults" class="searching inline">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          <div class="hint">Searching for "{{ lastTaskQuery || taskQuery }}"...</div>
        </div>

        <div *ngIf="showTaskEmptyState" class="empty">
          <h3>Start typing to search</h3>
          <p>Enter a task number and we'll open the matching task.</p>
        </div>

        <div *ngIf="invalidTaskQuery" class="empty">
          <h3>Task number only</h3>
          <p>This lookup currently supports task numbers only.</p>
        </div>

        <div *ngIf="showTaskNoResultsYet" class="empty">
          <h3>No task found</h3>
          <p>There isn't a task matching "{{ lastTaskQuery }}".</p>
        </div>

        <div *ngIf="isSearchingTasks && !showTaskResults" class="searching">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          <div class="hint">Searching for "{{ lastTaskQuery || taskQuery }}"...</div>
        </div>

        <div *ngIf="showTaskResults" class="results-list">
          <button type="button" class="result-card" *ngFor="let task of tasks" (click)="openTask(task)">
            <div class="result-main">
              <div class="kicker">Task</div>
              <div class="title">#{{ task.id }}</div>
              <div class="meta">
                <span>{{ task.customer?.name || 'No customer' }}</span>
                <span class="dot">&middot;</span>
                <span>{{ task.subject || 'No subject' }}</span>
              </div>
            </div>
            <div class="result-side">
              <div class="amount">{{ task.type || 'Task' }}</div>
              <div class="status" [class]="task.status">{{ task.status || 'unknown' }}</div>
            </div>
          </button>
        </div>
      </section>
    </section>

    <section class="search-panel">
      <div class="panel-header">
        <div class="panel-kicker">Customers</div>
        <h2>Search customer name</h2>
      </div>

      <section class="search-bar">
        <ap-search
          [fullWidth]="true"
          placeholder="customer name"
          [query]="customerQuery"
          (onChange)="onCustomerQueryChange($event)"
          (cancelled)="onCustomerCancelled()"
        ></ap-search>

        <ap-search
          [fullWidth]="true"
          placeholder="customer phone"
          [query]="customerPhoneQuery"
          (onChange)="onCustomerPhoneQueryChange($event)"
          (cancelled)="onCustomerPhoneCancelled()"
        ></ap-search>
      </section>

      <section class="results">
        <div *ngIf="isSearchingAnyCustomer && showCustomerResults" class="searching inline">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          <div class="hint">Updating customer matches...</div>
        </div>

        <div *ngIf="showCustomerEmptyState" class="empty">
          <h3>Start typing to search</h3>
          <p>Search customers by name or phone number.</p>
        </div>

        <div *ngIf="invalidCustomerPhoneQuery" class="empty">
          <h3>Phone number only</h3>
          <p>This lookup supports phone digits only.</p>
        </div>

        <div *ngIf="showCustomerNoResultsYet" class="empty">
          <h3>No customer found</h3>
          <p>No customer matched the current name or phone filters.</p>
        </div>

        <div *ngIf="isSearchingAnyCustomer && !showCustomerResults" class="searching">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          <div class="hint">Searching customers...</div>
        </div>

        <div *ngIf="showCustomerResults" class="results-list">
          <div class="result-card" *ngFor="let customer of combinedCustomers">
            <div class="result-main">
              <div class="kicker">Customer</div>
              <button type="button" class="title" (click)="openCustomer(customer)">{{ customer.name || customer.business || 'Unnamed customer' }}</button>
              <div class="meta">
                <span>{{ customer.business || 'No business' }}</span>
                <span class="dot">&middot;</span>
                <span [apPhone]="customer.phone">{{ (customer.phone | phone) || 'No phone' }}</span>
              </div>
            </div>
            <div class="result-side">
              <div class="amount">{{ customer.email || 'No email' }}</div>
              <button type="button" class="status neutral" (click)="openCustomer(customer)">Open</button>
            </div>
          </div>
        </div>
      </section>
    </section>

    <section class="search-panel">
      <div class="panel-header">
        <div class="panel-kicker">Revenue transactions</div>
        <h2>Search revenue</h2>
      </div>
      <section class="search-bar">
        <ap-search
          [fullWidth]="true"
          placeholder="invoice number or transaction text"
          [query]="revenueQuery"
          (onChange)="onRevenueQueryChange($event)"
          (cancelled)="onRevenueCancelled()"
        ></ap-search>
        <p class="hint">Search descriptions, notes, and linked invoice numbers in the Revenue account.</p>
      </section>
      <section class="results">
        <div *ngIf="!revenueQuery.trim()" class="empty">
          <h3>Find an invoice payment</h3>
          <p>Enter an invoice number or text from the bank transaction.</p>
        </div>
        <div *ngIf="isSearchingRevenue" class="searching">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          <div class="hint">Searching revenue transactions...</div>
        </div>
        <div *ngIf="revenueSearchError" class="empty">
          <h3>Could not search revenue</h3>
          <p>Please try your search again.</p>
        </div>
        <div *ngIf="revenueQuery.trim() && !isSearchingRevenue && !revenueSearchError && !revenueTransactions.length" class="empty">
          <h3>No revenue transactions found</h3>
          <p>No Revenue account transactions matched "{{ revenueQuery }}".</p>
        </div>
        <div *ngIf="revenueTransactions.length" class="results-list">
          <p class="hint" *ngIf="revenueTransactions.length === 100">Showing the latest 100 matches. Refine your search to narrow the results.</p>
          <button type="button" class="result-card" *ngFor="let transaction of revenueTransactions" (click)="openRevenueTransaction(transaction)">
            <div class="result-main">
              <div class="kicker">Revenue transaction</div>
              <div class="title">{{ transaction.description || 'Transaction' }}</div>
              <div class="meta">
                <span>{{ transaction.date | date:'dd MMM yyyy' }}</span>
                <span *ngIf="transaction.invoice?.number">Invoice #{{ transaction.invoice.number }}</span>
              </div>
              <div class="meta" *ngIf="transaction.note"><span>{{ transaction.note }}</span></div>
            </div>
            <div class="result-side">
              <div class="amount">{{ transaction.amount | human }}</div>
              <div class="status neutral">{{ transaction.status || 'Revenue' }}</div>
            </div>
          </button>
        </div>
      </section>
    </section>

    <section class="search-panel">
      <div class="panel-header">
        <div class="panel-kicker">Transactions</div>
        <h2>Search expense amount</h2>
      </div>

      <section class="search-bar">
        <ap-search
          [fullWidth]="true"
          placeholder="expense amount"
          [query]="transactionAmountQuery"
          (onChange)="onTransactionAmountQueryChange($event)"
          (cancelled)="onTransactionAmountCancelled()"
        ></ap-search>

        <mat-form-field appearance="outline" class="year-filter">
          <mat-label>Filter by year</mat-label>
          <input
            matInput
            type="number"
            placeholder="2026"
            [(ngModel)]="transactionYearQuery"
            (ngModelChange)="onTransactionYearQueryChange($event)">
        </mat-form-field>
      </section>

      <section class="results">
        <div *ngIf="isSearchingTransactionsByAmount && showTransactionResults" class="searching inline">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          <div class="hint">Updating expense transaction matches...</div>
        </div>

        <div *ngIf="showTransactionEmptyState" class="empty">
          <h3>Start typing to search</h3>
          <p>Enter an amount to find matching expense transactions.</p>
        </div>

        <div *ngIf="invalidTransactionAmountQuery" class="empty">
          <h3>Amount only</h3>
          <p>This lookup supports transaction amounts only.</p>
        </div>

        <div *ngIf="invalidTransactionYearQuery" class="empty">
          <h3>Year only</h3>
          <p>Enter a four digit year, for example 2026.</p>
        </div>

        <div *ngIf="showTransactionNoResultsYet" class="empty">
          <h3>No expense transaction found</h3>
          <p>No expense transaction matched "{{ lastTransactionAmountQuery }}"{{ lastTransactionYearQuery ? ' in ' + lastTransactionYearQuery : '' }}.</p>
        </div>

        <div *ngIf="isSearchingTransactionsByAmount && !showTransactionResults" class="searching">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          <div class="hint">Searching expense transactions...</div>
        </div>

        <div *ngIf="showTransactionResults" class="results-list">
          <button type="button" class="result-card" *ngFor="let transaction of transactionsByAmount" (click)="openTransaction(transaction)">
            <div class="result-main">
              <div class="kicker">Expense transaction</div>
              <div class="title">{{ transaction.description || transaction.note || 'Transaction' }}</div>
              <div class="meta">
                <span>{{ transaction.supplier?.business || transaction.preset || transaction.subcategory || 'Uncategorised' }}</span>
                <span class="dot">&middot;</span>
                <span>{{ transaction.date | date:'dd MMM yyyy' }}</span>
              </div>
            </div>
            <div class="result-side">
              <div class="amount">{{ transaction.amount | human }}</div>
              <div class="status neutral">{{ transaction.atocategory || 'Expense' }}</div>
            </div>
          </button>
        </div>
      </section>
    </section>
  </div>
</div>
